import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, firstValueFrom, map, switchMap, take, tap } from 'rxjs';

import { BasicInfoData, LocationData, SearchApiService } from '@hiptraveler/data-access/api';
import { UserState } from '@hiptraveler/data-access/user';
import { SearchAction, SearchState } from '@hiptraveler/data-access/search';
import { ItineraryAction, ItineraryState } from '@hiptraveler/data-access/itinerary';
import { AppListenerService, currentLang, getPathname, getWindowRef, NavbarControlStateService, removeIgnoredEndpoints, requestEndpointParamValue, SearchLocationService, SearchPageControlStateService, updateIgnoredEndpoints, WINDOW } from '@hiptraveler/common';

const SEARCH_PATHS: string[] = [ 'search','thingstodo','hotels','foodanddrink' ];
const ENDPOINTS_TO_IGNORE: string[] = [ '/trip-planner/search/autocomplete', '/trip-planner/search/location' ];


@Injectable()
export class NavigationChangeObserverService {

  signedOut: boolean = false;
  
  constructor(
    private router: Router,
    private location: Location,
    private store: Store,
    private searchApi: SearchApiService,
    private appListener: AppListenerService,
    private navbarControl: NavbarControlStateService,
    private searchLocation: SearchLocationService,
    private searchPageControl: SearchPageControlStateService
  ) { }

  private get searchPage(): boolean {
    return this.searchLocation.rootSearchRoute
      || getWindowRef()?.location?.pathname?.includes(`/${currentLang()}/search/`)
  }

  observe(): void {

    this.signedOut = false;

    this.initializeAuthStatusOnItineraryRoutes();
    this.initializeItineraryAutocompleteFlow();
    this.syncStateOnNavigationEnd();
  }

  initializeAuthStatusOnItineraryRoutes(): void {
    this.store.select(UserState.authenticated).subscribe((state: boolean) => {
      const pathname = getWindowRef()?.location?.pathname;
      if (!pathname?.match(new RegExp(`/${currentLang()}/(itinerary|travel-story)/`))) return;

      this.signedOut = !state;
    });
  }

  initializeItineraryAutocompleteFlow(): void {
    this.appListener.previousUrl$.pipe(
      map((previousUrl: string) => {
        // 1. Only proceed if we came from an itinerary page
        if (!previousUrl.includes(`/${currentLang()}/itinerary/`)) return;

        // 2. Maintain ignored endpoints in sessionStorage
        updateIgnoredEndpoints(ENDPOINTS_TO_IGNORE);

        // 3. Strip any of the search prefixes from the current path
        const pathname = getPathname(this.location.path());
        const rx = new RegExp(`/${currentLang()}/(?:${SEARCH_PATHS.join('|')})/`);
        return rx.test(pathname) ? pathname.replace(rx, '') : undefined;
      }),
      filter(Boolean),
      switchMap((placename: string) => this.searchApi.getAutocompleteLocation({ q: decodeURIComponent(placename) }).pipe(
        map(e => ({ q: e.locations[0].city, locId: e.locations[0].id })),
        switchMap(({ q, locId }) => this.store.dispatch(new SearchAction.GetLocationDataByQuery({ q, locId }))),
        switchMap(() => this.store.select(SearchState.locationData)),
        tap((location: LocationData | null) => this.searchLocation.updateSearchLocation({
          name: location?.city,
          location: location?.formattedAddr || '',
          locId: location?.id || location?.gPlaceId || location?.countryId || '',
          latitude: +(location?.latitude || '0'),
          longitude: +(location?.longitude || '0'),
          type: location?.type || ''
        }))
      )),
      tap(() => {
        // Load, filter out unwanted endpoints, and save back
        removeIgnoredEndpoints(ENDPOINTS_TO_IGNORE);
      })
    ).subscribe();
  }

  syncStateOnNavigationEnd(): void {
    this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
      map(e => e.url)
    ).subscribe(async (activeUrl: string) => {

      const previousUrl = this.appListener.previousUrl$$.value;
      const composePagePrevUrl = previousUrl.includes(`/${currentLang()}/compose/`);
      const itineraryViewPagePrevUrl = previousUrl.includes(`/${currentLang()}/itinerary/`);
      const basicInfo = this.store.selectSnapshot(ItineraryState.basicInfo);

      const activity: any = this.store.selectSnapshot(ItineraryState.actDateMap)?.['1'];

      if (activeUrl.includes(`/${currentLang()}/compose/`)) {

        const previousDay = this.searchPageControl.activityDate?.day
          || this.searchPageControl.replaceItineraryActivity?.day
          || this.searchPageControl.replaceChillTimeActivity?.day || 0;

        this.searchPageControl.previousDay$$.next(previousDay);
        this.searchPageControl.activityDate$$.next(null);
        this.searchPageControl.replaceItineraryActivity$$.next(null);
        this.searchPageControl.replaceChillTimeActivity$$.next(null);
        return;
      }

      if (new RegExp(`/${currentLang()}/(itinerary|travel-story)/`).test(activeUrl)) {
        const pageTitle = activeUrl.split('/')[3];
        this.store.select(ItineraryState.basicInfo).pipe(
          filter(Boolean),
          filter(e => e?.pageTitle === pageTitle),
          tap((basicInfo: Partial<BasicInfoData>) => {
            const itineraries = this.store.selectSnapshot(UserState.itineraries);
            const itinerary = itineraries?.find(e => e.pageTitle === basicInfo?.pageTitle);
            itinerary && this.searchPageControl.activityDate$$.next(null);
          }),
          take(1)
        ).subscribe();
        return;
      }

      if (this.searchPage && activity?.pageTitle && !basicInfo) {
        try {
          await firstValueFrom(this.store.dispatch(new ItineraryAction.GetBasicInfoByPageTitle(activity.pageTitle)));
          const param = getWindowRef()?.[requestEndpointParamValue] === 'blog' ? 'travel-story' : 'itinerary';
          this.navbarControl.setActiveNavbarButtons([{
            name: 'navbar.action-text-open-trip',
            url: `${currentLang()}/compose/${param}/${activity?.pageTitle}`,
            closeButton: true
          }]);
        } finally { }
      }

      if ((composePagePrevUrl || itineraryViewPagePrevUrl) && basicInfo?.pageTitle && activity && !this.signedOut) {
        const subject = this.searchPageControl.activityDate$$;
        activity['pageTitle'] = basicInfo.pageTitle;
        subject.value || subject.next(activity);
        const param = basicInfo.type === 'blog' ? 'travel-story' : 'itinerary';
        this.navbarControl.setActiveNavbarButtons([{
          name: 'navbar.action-text-open-trip',
          url: `${currentLang()}/compose/${param}/${basicInfo.pageTitle}`,
          closeButton: true
        }]);
      }
    });
  }

}
